import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./EsignDigio.css";

const EsignDigio = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [base64PDF, setBase64PDF] = useState("");
  const [digioInstance, setDigioInstance] = useState(null);
  const [events, setEvents] = useState([]);
  const [clientType, setClientType] = useState(null); // null, 'existing', 'new'
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    contactNumber: "",
    panNumber: "",
    agentName: "",
    address: "",
    selectedPlan: "",
    amountPaid: ""
  });
  const [isFormValid, setIsFormValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const responseRef = useRef(null);

  // Plan options for dropdown
  const planOptions = [
    { value: "", label: "Select a plan" },
    { value: "basic", label: "Basic Plan" },
    { value: "premium", label: "Premium Plan" },
    { value: "hni", label: "HNI Plan" },
    { value: "customized", label: "Customized Plan" }
  ];

  // ✅ Load Digio SDK dynamically
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://app.digio.in/sdk/v11/digio.js";
    script.async = true;
    script.onload = () => {
      console.log("✅ Digio SDK loaded");
      initDigio();
    };
    script.onerror = () => {
      console.error("❌ Failed to load Digio SDK script");
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Initialize Digio SDK after script load
  const initDigio = () => {
    if (window.Digio) {
      const options = {
        environment: "production", // switch to "production" in prod
        is_redirection_approach: true, // 🔑 open in same page
        redirect_url: "https://trillionstockresearch.com/success", // 🔑 redirect after signing
        is_iframe: false, // 🔑 not in iframe
        callback: (response) => {
          console.log("🔔 Digio Callback Response:", response);
          if (response.error_code) {
            setErrorMessage("Error: " + response.message);
            setSuccessMessage("");
          } else {
            setSuccessMessage("eSign completed successfully!");
            setErrorMessage("");
          }
        },
        theme: {
          primaryColor: "#3a9135",
          secondaryColor: "#000000",
        },
        event_listener: (event) => {
          console.log("📩 Event:", event.event);
          setEvents((prev) => [...prev, `Event: ${event.event}`]);
        },
      };

      setDigioInstance(new window.Digio(options));
    }
  };

  // ✅ Handle form input changes
  const handleInputChange = (field, value) => {
    // Restrict number inputs to only accept numeric characters
    if (field === 'contactNumber') {
      // Only allow digits for contact number
      const numericValue = value.replace(/\D/g, '');
      setFormData(prev => ({
        ...prev,
        [field]: numericValue
      }));
    } else if (field === 'amountPaid') {
      // Only allow digits and decimal point for amount
      const numericValue = value.replace(/[^0-9.]/g, '');
      // Ensure only one decimal point
      const parts = numericValue.split('.');
      const formattedValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;
      setFormData(prev => ({
        ...prev,
        [field]: formattedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Handle key down events to prevent non-numeric input
  const handleKeyDown = (field, event) => {
    if (field === 'contactNumber') {
      // Allow: backspace, delete, tab, escape, enter, home, end, left, right, up, down
      if ([8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(event.keyCode) !== -1 ||
          // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
          (event.keyCode === 65 && event.ctrlKey === true) ||
          (event.keyCode === 67 && event.ctrlKey === true) ||
          (event.keyCode === 86 && event.ctrlKey === true) ||
          (event.keyCode === 88 && event.ctrlKey === true)) {
        return;
      }
      // Ensure that it is a number and stop the keypress
      if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) && (event.keyCode < 96 || event.keyCode > 105)) {
        event.preventDefault();
      }
    } else if (field === 'amountPaid') {
      // Allow: backspace, delete, tab, escape, enter, home, end, left, right, up, down
      if ([8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(event.keyCode) !== -1 ||
          // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
          (event.keyCode === 65 && event.ctrlKey === true) ||
          (event.keyCode === 67 && event.ctrlKey === true) ||
          (event.keyCode === 86 && event.ctrlKey === true) ||
          (event.keyCode === 88 && event.ctrlKey === true)) {
        return;
      }
      // Allow decimal point (keyCode 190 or 110)
      if (event.keyCode === 190 || event.keyCode === 110) {
        // Check if decimal point already exists
        if (formData.amountPaid.includes('.')) {
          event.preventDefault();
        }
        return;
      }
      // Ensure that it is a number and stop the keypress
      if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) && (event.keyCode < 96 || event.keyCode > 105)) {
        event.preventDefault();
      }
    }
  };

  // ✅ Validate form inputs based on client type
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[6-9]\d{9}$/;
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    
    const isValidEmail = emailRegex.test(formData.email);
    const isValidPhone = phoneRegex.test(formData.contactNumber);
    const isValidPan = panRegex.test(formData.panNumber);
    
    if (clientType === 'existing') {
      // For existing clients, validate PAN, phone, and transaction details (no Digio required)
      const isTransactionFieldsFilled = formData.panNumber.trim() !== "" && 
                                       formData.contactNumber.trim() !== "" &&
                                       formData.agentName.trim() !== "" &&
                                       formData.selectedPlan.trim() !== "" &&
                                       formData.amountPaid.trim() !== "";
      setIsFormValid(isValidPan && isValidPhone && isTransactionFieldsFilled);
    } else if (clientType === 'new') {
      // For new clients, validate all fields and require Digio
      const isAllFieldsFilled = Object.values(formData).every(value => value.trim() !== "");
      setIsFormValid(isValidEmail && isValidPhone && isValidPan && isAllFieldsFilled && base64PDF && digioInstance);
    } else {
      setIsFormValid(false);
    }
  }, [formData, base64PDF, digioInstance, clientType]);

  // ✅ Load PDF as Base64 from public/tandc.txt
  useEffect(() => {
    fetch("/tandc.txt")
      .then((res) => res.text())
      .then((data) => {
        setBase64PDF(data.trim());
        setLoading(false);
      })
      .catch((err) => {
        console.error("❌ Error loading tandc.txt:", err);
        setLoading(false);
      });
  }, []);

  // ✅ Handle form submission
  const handleEsign = async () => {
    if (!isFormValid) {
      setErrorMessage("Please fill in all required fields with valid information.");
      return;
    }

    try {
      setLoading(true);
      setEvents([]); // clear old events
      setErrorMessage("");
      setSuccessMessage("");

      if (clientType === 'existing') {
        // For existing clients, just submit the form data without eSign
        const response = await axios.post("https://tstock.trillionstechsolutions.com/payment/store-details", {
          clientType: 'existing',
          formData: {
            panNumber: formData.panNumber,
            contactNumber: formData.contactNumber,
            agentName: formData.agentName,
            selectedPlan: formData.selectedPlan,
            amountPaid: formData.amountPaid
          }
        });

        if (response.data.success) {
          // Redirect to success page for existing customers
          navigate("/success");
        } else {
          setErrorMessage(response.data.message || "Failed to submit transaction details. Please try again.");
        }
      } else {
        // For new clients, proceed with eSign
        const requestData = {
          fileName: "terms_and_conditions.pdf",
          fileContentBase64: base64PDF,
          clientType: clientType,
          formData: formData,
          signatoryEmail: formData.email,
          signatoryName: formData.fullName
        };

        // 🔗 Call your Express backend for eSign
        const response = await axios.post("https://tstock.trillionstechsolutions.com/digio-esign/esign", requestData);

      const { digio_doc_id, access_token } = response.data;

      if (!digio_doc_id || !access_token) {
          setErrorMessage("Missing digio_doc_id or access_token from backend!");
        return;
      }

      // 🚀 Start eSign flow with redirection
      digioInstance.init();
        digioInstance.submit(digio_doc_id, requestData.signatoryEmail, access_token);
      }
    } catch (error) {
      console.error("❌ Error processing request:", error.response?.data || error.message);
      if (clientType === 'existing') {
        // Handle specific error responses from backend
        if (error.response?.status === 404) {
          setErrorMessage("Customer not found. Please ensure the PAN number and contact number are correct.");
        } else if (error.response?.data?.message) {
          setErrorMessage(error.response.data.message);
        } else {
          setErrorMessage("Failed to submit transaction details. Please try again.");
        }
      } else {
        setErrorMessage("Failed to start eSign, please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="esign-digio-container">
      <div className="esign-digio-main-content">
        <div className="esign-digio-header">
          <h1>Digital Signature</h1>
          <p>Please provide your details to electronically sign the Terms and Conditions</p>
        </div>

        <div className="esign-digio-content">
          <div className="esign-digio-form-container">
            {!clientType ? (
              // Client Type Selection
              <div className="esign-digio-client-selection">
                <h2>Are you already a client with Trillions?</h2>
                <div className="esign-digio-client-options">
                  <button
                    type="button"
                    onClick={() => setClientType('existing')}
                    className="esign-digio-client-option"
                  >
                    <div className="esign-digio-option-icon">👤</div>
                    <h3>Already a Client</h3>
                    <p>I have an existing account with Trillions</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setClientType('new')}
                    className="esign-digio-client-option"
                  >
                    <div className="esign-digio-option-icon">🆕</div>
                    <h3>New to Trillions</h3>
                    <p>I'm a new customer signing up</p>
                  </button>
                </div>
              </div>
            ) : (
              // Form based on client type
              <form className="esign-digio-form">
                {clientType === 'existing' ? (
                  // Existing Client Form
                  <>
                    <div className="esign-digio-form-section">
                      <h3>Client Information</h3>
                      <div className="esign-digio-form-row">
                        <div className="esign-digio-form-group">
                          <label htmlFor="panNumber">PAN Number *</label>
                          <input
                            type="text"
                            id="panNumber"
                            value={formData.panNumber}
                            onChange={(e) => handleInputChange('panNumber', e.target.value.toUpperCase())}
                            placeholder="Enter your PAN number (e.g., ABCDE1234F)"
                            maxLength="10"
                            required
                          />
                        </div>

                        <div className="esign-digio-form-group">
                          <label htmlFor="contactNumber">Contact Number *</label>
                          <input
                            type="tel"
                            id="contactNumber"
                            value={formData.contactNumber}
                            onChange={(e) => handleInputChange('contactNumber', e.target.value)}
                            onKeyDown={(e) => handleKeyDown('contactNumber', e)}
                            placeholder="Enter your 10-digit mobile number"
                            maxLength="10"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="esign-digio-form-section">
                      <h3>Transaction Details</h3>
                      <div className="esign-digio-form-row">
                        <div className="esign-digio-form-group">
                          <label htmlFor="agentName">Agent Name *</label>
                          <input
                            type="text"
                            id="agentName"
                            value={formData.agentName}
                            onChange={(e) => handleInputChange('agentName', e.target.value)}
                            placeholder="Enter agent name"
                            required
                          />
                        </div>

                        <div className="esign-digio-form-group">
                          <label htmlFor="selectedPlan">Selected Plan *</label>
                          <select
                            id="selectedPlan"
                            value={formData.selectedPlan}
                            onChange={(e) => handleInputChange('selectedPlan', e.target.value)}
                            required
                            className="esign-digio-select"
                          >
                            {planOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="esign-digio-form-group">
                        <label htmlFor="amountPaid">Amount Paid *</label>
                        <input
                          type="number"
                          id="amountPaid"
                          value={formData.amountPaid}
                          onChange={(e) => handleInputChange('amountPaid', e.target.value)}
                          onKeyDown={(e) => handleKeyDown('amountPaid', e)}
                          placeholder="Enter amount paid"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  // New Client Form (Complete Form)
                  <>
                    {/* Personal Information */}
                    <div className="esign-digio-form-section">
                      <h3>Personal Information</h3>
                      <div className="esign-digio-form-row">
                        <div className="esign-digio-form-group">
                          <label htmlFor="fullName">Full Name *</label>
                          <input
                            type="text"
                            id="fullName"
                            value={formData.fullName}
                            onChange={(e) => handleInputChange('fullName', e.target.value)}
                            placeholder="Enter your full name"
                            required
                          />
                        </div>

                        <div className="esign-digio-form-group">
                          <label htmlFor="email">Email Address *</label>
                          <input
                            type="email"
                            id="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            placeholder="Enter your email address"
                            required
                          />
                        </div>
                      </div>

                      <div className="esign-digio-form-row">
                        <div className="esign-digio-form-group">
                          <label htmlFor="contactNumber">Contact Number *</label>
                          <input
                            type="tel"
                            id="contactNumber"
                            value={formData.contactNumber}
                            onChange={(e) => handleInputChange('contactNumber', e.target.value)}
                            onKeyDown={(e) => handleKeyDown('contactNumber', e)}
                            placeholder="Enter your 10-digit mobile number"
                            maxLength="10"
                            required
                          />
                        </div>

                        <div className="esign-digio-form-group">
                          <label htmlFor="panNumber">PAN Number *</label>
                          <input
                            type="text"
                            id="panNumber"
                            value={formData.panNumber}
                            onChange={(e) => handleInputChange('panNumber', e.target.value.toUpperCase())}
                            placeholder="Enter your PAN number (e.g., ABCDE1234F)"
                            maxLength="10"
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Transaction Information */}
                    <div className="esign-digio-form-section">
                      <h3>Transaction Information</h3>
                      <div className="esign-digio-form-row">
                        <div className="esign-digio-form-group">
                          <label htmlFor="agentName">Agent Name *</label>
                          <input
                            type="text"
                            id="agentName"
                            value={formData.agentName}
                            onChange={(e) => handleInputChange('agentName', e.target.value)}
                            placeholder="Enter agent name"
                            required
                          />
                        </div>

                        <div className="esign-digio-form-group">
                          <label htmlFor="selectedPlan">Selected Plan *</label>
                          <select
                            id="selectedPlan"
                            value={formData.selectedPlan}
                            onChange={(e) => handleInputChange('selectedPlan', e.target.value)}
                            required
                            className="esign-digio-select"
                          >
                            {planOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="esign-digio-form-group">
                        <label htmlFor="amountPaid">Amount Paid *</label>
                        <input
                          type="number"
                          id="amountPaid"
                          value={formData.amountPaid}
                          onChange={(e) => handleInputChange('amountPaid', e.target.value)}
                          onKeyDown={(e) => handleKeyDown('amountPaid', e)}
                          placeholder="Enter amount paid"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                    </div>

                    {/* Address Information */}
                    <div className="esign-digio-form-section">
                      <h3>Address Information</h3>
                      <div className="esign-digio-form-group">
                        <label htmlFor="address">Address *</label>
                        <textarea
                          id="address"
                          value={formData.address}
                          onChange={(e) => handleInputChange('address', e.target.value)}
                          placeholder="Enter your complete address"
                          rows="3"
                          required
                          className="esign-digio-textarea"
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className="esign-digio-terms-section">
                  <h3>Terms and Conditions</h3>
                  <div className="esign-digio-terms-content">
                    {clientType === 'existing' ? (
                      <p>
                        By submitting your transaction details, you confirm that the information provided is accurate 
                        and you agree to our Terms and Conditions for processing your request.
                      </p>
                    ) : (
                      <p>
                        By proceeding with the digital signature, you agree to our Terms and Conditions. 
                        The document will be electronically signed and legally binding.
                      </p>
                    )}
                    <div className="esign-digio-file-info">
                      <p>📄 Document: Terms and Conditions Agreement</p>
                      {clientType === 'new' && <p>🔒 Secure eSign process powered by Digio</p>}
                      {clientType === 'existing' && <p>📋 Simple form submission for existing clients</p>}
                    </div>
                  </div>
                </div>

                {errorMessage && (
                  <div className="esign-digio-error-message">
                    <span>⚠️</span>
                    <span>{errorMessage}</span>
                  </div>
                )}

                {successMessage && (
                  <div className="esign-digio-success-message">
                    <span>✅</span>
                    <span>{successMessage}</span>
                  </div>
                )}

                <div className="esign-digio-form-actions">
                  {clientType && (
                    <button
                      type="button"
                      onClick={() => setClientType(null)}
                      className="esign-digio-back-button"
                    >
                      ← Back to Selection
                    </button>
                  )}
                  
          <button
                    type="button"
            onClick={handleEsign}
                    disabled={!isFormValid || loading}
                    className="esign-digio-button"
                  >
                    {loading ? (
                      <>
                        <div className="esign-digio-spinner"></div>
                        Processing...
                      </>
                    ) : clientType === 'existing' ? (
                      "Submit Transaction Details"
                    ) : (
                      "Sign Terms & Conditions"
                    )}
          </button>
        </div>
              </form>
            )}

        {events.length > 0 && (
              <div className="esign-digio-events-log">
                <h3>Process Events:</h3>
                <div ref={responseRef} className="esign-digio-events-container">
              {events.map((event, index) => (
                    <p key={index} className="esign-digio-event-item">
                  {event}
                </p>
              ))}
            </div>
          </div>
        )}

            <div className="esign-digio-status-info">
              <p>
                Status: {loading ? "Processing..." : 
                  clientType === 'existing' ? "✅ Ready to submit" :
                  digioInstance ? "✅ Ready to sign" : "⏳ Initializing..."}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EsignDigio;
